import React, { memo, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { Icon, Tabs } from '@Sage/hoi-poi-ui';
import { Route, Routes } from 'react-router-dom';

import VideoModal from 'components/VideoModal';
import { getLiteral } from 'utils/getLiteral';
import { RouteOnMount, ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';

import SettingsLayout from '../components/SettingsLayout';
import AutomationBuilder from './components/AutomationBuilder';
import AutomationsList from './AutomationsList';
import AutomationsLog from './AutomationsLog';
import AutomationsTemplates from './AutomationsTemplates';

import './styles.scss';

const AutomationSettings = memo(() => {
    const modalRef = useRef(null);

    const [activeTab, setActiveTab] = useState('automations-list');
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    const onModalRef = useCallback((ref) => {
        modalRef.current = ref;
    }, []);

    const handleOpenVideoModal = useCallback(() => {
        setIsVideoModalOpen(true);
    }, []);

    const handleCloseVideoModal = useCallback(() => {
        setIsVideoModalOpen(false);
    }, []);

    const openBuilder = useCallback((...props) => {
        modalRef.current?.open(...props);
    }, []);

    const tabs = useMemo(
        () => [
            {
                key: 'automations-list',
                title: getLiteral('label_automations'),
            },
            {
                key: 'automations-log',
                title: getLiteral('label_automation_history'),
            },
            {
                key: 'automations-templates',
                title: getLiteral('label_automations_templates'),
            },
        ],
        [],
    );

    useEffect(() => {
        logEvent({
            event: 'settings',
            submodule: 'automations',
            functionality: 'listView',
        });
    }, []);

    const goToTemplates = useCallback(() => {
        ensureRoute('/settings/automations/templates');
    }, []);

    const learnMoreItems = useMemo(() => {
        return [
            {
                icon: <Icon name="playCircleFilled" />,
                onClick: handleOpenVideoModal,
                title: getLiteral('action_automations_video_tutorial'),
            },
            {
                icon: <Icon name="helpOutline" />,
                onClick: () => {
                    window.open(getLiteral('label_automations_knowledge_base_all_links'), '_blank');
                },
                title: getLiteral('action_automations_knowledge_base'),
            },
        ];
    }, [handleOpenVideoModal]);

    return (
        <SettingsLayout
            className="fm-automation-settings"
            title={getLiteral('label_automations')}
            description={getLiteral('label_automations_desc')}
            withoutDivider
            learnMoreItems={learnMoreItems}
        >
            <Tabs
                onChange={(key) => {
                    setActiveTab(key);
                    switch (key) {
                        case 'automations-list':
                            ensureRoute('/settings/automations');
                            break;
                        case 'automations-log':
                            ensureRoute('/settings/automations/logs');
                            break;
                        case 'automations-templates':
                            ensureRoute('/settings/automations/templates');
                            break;
                        default:
                            break;
                    }
                }}
                activeKey={activeTab}
                tabs={tabs}
            />
            {activeTab === 'automations-list' && (
                <AutomationsList openBuilder={openBuilder} goToTemplates={goToTemplates} />
            )}
            {activeTab === 'automations-log' && (
                <AutomationsLog openBuilder={openBuilder} goToTemplates={goToTemplates} />
            )}
            {activeTab === 'automations-templates' && (
                <AutomationsTemplates openBuilder={openBuilder} />
            )}
            <AutomationBuilder onRef={onModalRef} />
            <VideoModal
                isOpen={isVideoModalOpen}
                onRequestClose={handleCloseVideoModal}
                title={getLiteral('action_automations_video_tutorial')}
                videoUrl={getLiteral('label_automations_video_tutorial_link')}
            />
            <Routes>
                <Route
                    path="/"
                    element={
                        <RouteOnMount
                            onMount={() => {
                                setActiveTab('automations-list');
                            }}
                        />
                    }
                />
                <Route
                    path="/logs"
                    element={
                        <RouteOnMount
                            onMount={() => {
                                setActiveTab('automations-log');
                            }}
                        />
                    }
                />
                <Route
                    path="/templates"
                    element={
                        <RouteOnMount
                            onMount={() => {
                                setActiveTab('automations-templates');
                            }}
                        />
                    }
                />
                <Route
                    path="/:id"
                    element={
                        <RouteOnMount
                            onMount={(params) => {
                                if (!params.id) return;
                                modalRef.current?.open(params.id);
                            }}
                        />
                    }
                />
            </Routes>
        </SettingsLayout>
    );
});

export default AutomationSettings;
